import React, { useRef, useState, useEffect } from "react";
import { Play, Pause, FileText, Loader2 } from "lucide-react";
import { API_URL } from "../config";

function fmt(sec) {
  if (!isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function VoicePlayer({ src, accent = "#007c84", messageId, token, transcript, onTranscribed }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onLoaded = () => setDuration(audio.duration || 0);
    const onTime = () => setCurrent(audio.currentTime || 0);
    const onEnd = () => { setPlaying(false); setCurrent(0); };
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnd);
    };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) { audio.pause(); setPlaying(false); }
    else { audio.play(); setPlaying(true); }
  };

  const transcribe = async () => {
    if (!messageId || !token) return;
    setTranscribing(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/chat/messages/${messageId}/transcribe`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Не удалось расшифровать");
      onTranscribed?.(data.transcript);
    } catch (err) {
      setError(err.message);
    } finally {
      setTranscribing(false);
    }
  };

  const progress = duration ? current / duration : 0;

  return (
    <div style={{ minWidth: 180, padding: "2px 2px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <audio ref={audioRef} src={src} preload="metadata" />
        <button
          onClick={toggle}
          style={{
            width: 30, height: 30, padding: 0, borderRadius: "50%", flexShrink: 0, border: "none", cursor: "pointer",
            background: accent, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {playing ? <Pause size={14} strokeWidth={2.4} fill="currentColor" /> : <Play size={14} strokeWidth={2.4} fill="currentColor" style={{ marginLeft: 1 }} />}
        </button>
        <div style={{ flex: 1, minWidth: 70 }}>
          <div style={{ height: 3, borderRadius: 2, background: "rgba(0,0,0,0.12)", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progress * 100}%`, background: accent, borderRadius: 2 }} />
          </div>
          <div style={{ fontSize: "0.72rem", color: "#888", marginTop: 2 }}>
            {fmt(playing || current > 0 ? current : duration)}
          </div>
        </div>
        {!transcript && messageId && (
          <button
            onClick={transcribe}
            disabled={transcribing}
            title="Расшифровать в текст"
            style={{
              background: "none", border: "none", cursor: "pointer", color: accent,
              display: "flex", alignItems: "center", padding: 4, opacity: transcribing ? 0.5 : 1, flexShrink: 0,
            }}
          >
            {transcribing ? <Loader2 size={15} className="spin" strokeWidth={2.2} /> : <FileText size={15} strokeWidth={2.2} />}
          </button>
        )}
      </div>
      {error && <div style={{ fontSize: "0.75rem", color: "#c0392b", marginTop: 4 }}>{error}</div>}
      {transcript && (
        <div style={{ fontSize: "0.83rem", color: "#444", marginTop: 6, paddingTop: 6, borderTop: "1px solid rgba(0,0,0,0.08)", lineHeight: 1.4 }}>
          {transcript}
        </div>
      )}
    </div>
  );
}
